import { ref,  onMounted } from 'vue'

// Storage key pour localStorage
const THEME_STORAGE_KEY = 'frontend-theme'

// État global partagé entre tous les composants
const currentTheme = ref(localStorage.getItem(THEME_STORAGE_KEY) || 'auto')
const isDark = ref(true)

/**
 * Composable pour gérer le thème light/dark
 * @returns {Object} { currentTheme, isDark, setTheme, getSystemTheme }
 */
export function useTheme() {
  /**
   * Détecte le thème système
   * @returns {string} 'dark' ou 'light'
   */
  const getSystemTheme = () => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }

  /**
   * Applique le thème sur le document
   * @param {string} theme - 'dark', 'light', ou 'auto'
   */
  const applyTheme = (theme) => {
    // Détermine le thème effectif
    const effectiveTheme = theme === 'auto' ? getSystemTheme() : theme
    isDark.value = effectiveTheme === 'dark'

    // Applique les classes sur <html>
    if (isDark.value) {
      document.documentElement.classList.add('dark')
      document.documentElement.classList.remove('light')
    } else {
      document.documentElement.classList.add('light')
      document.documentElement.classList.remove('dark')
    }
  }

  /**
   * Change le thème
   * @param {string} theme - 'dark', 'light', ou 'auto'
   */
  const setTheme = (theme) => {
    currentTheme.value = theme
    localStorage.setItem(THEME_STORAGE_KEY, theme)
    applyTheme(theme)
  }

  /**
   * Écoute les changements de préférences système
   */
  const watchSystemTheme = () => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handler = () => {
      if (currentTheme.value === 'auto') {
        applyTheme('auto')
      }
    }

    mediaQuery.addEventListener('change', handler)

    // Cleanup
    return () => mediaQuery.removeEventListener('change', handler)
  }

  // Initialisation au montage du composant
  onMounted(() => {
    applyTheme(currentTheme.value)
    watchSystemTheme()
  })

  return {
    currentTheme,
    isDark,
    setTheme,
    getSystemTheme
  }
}
